// =======================
// src/paginas/perfil/perfilUsuario.js
// =======================

import React, { useEffect, useState } from "react";

import {
  Alert,
  Box,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";

import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

import { api } from "@api/api";
import {
  coresTextosGlobais, tamanhosControlesGlobais,
  tamanhosModaisGlobais,
  tamanhosPaginasAdministrativas,
  textosGlobais,
} from "../../estilos/global";

import DadosUsuario from "../../componentes/perfil/DadosUsuario";
import SegurancaUsuario from "../../componentes/perfil/SegurancaUsuario";
import TransicaoPagina from "../../componentes/comuns/TransicaoPagina";

export default function PerfilUsuario() {
  const [perfil, setPerfil] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  useEffect(() => {
    const carregarPerfil = async () => {
      try {
        setCarregando(true);
        setErro("");

        const response = await fetch(api.auth(), {
          credentials: "include",
        });

        const resultado = await response.json().catch(() => null);

        if (!response.ok) {
          const mensagem = Array.isArray(resultado?.message)
            ? resultado.message.join(" ")
            : (resultado?.message ?? resultado?.msg);

          throw new Error(
            mensagem || "Não foi possível carregar os dados do usuário.",
          );
        }

        setPerfil(resultado);
      } catch (error) {
        setErro(
          error instanceof Error
            ? error.message
            : "Não foi possível carregar os dados do usuário.",
        );
      } finally {
        setCarregando(false);
      }
    };

    carregarPerfil();
  }, []);

  const atualizarPerfilLocal = (dadosAtualizados) => {
    setPerfil((perfilAtual) => ({
      ...perfilAtual,
      ...dadosAtualizados,
    }));
  };

  return (
    <Box
      component={TransicaoPagina}
      variante="navegacao"
      carregando={carregando}
      carregamento={
        <Box
          sx={{
            minHeight: { xs: 260, md: 0 },
            flex: "1 1 auto",
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress size={34} />
        </Box>
      }
      sx={{
        width: "100%",
        py: 0,
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,

        "& .MuiTextField-root .MuiOutlinedInput-root": {
          minHeight: 36,
          lineHeight: 1.4,
          "& .MuiOutlinedInput-input": {
            height: "1.4em",
            py: "7px",
          },
          "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--sear-borda-forte, #087cf0)",
          },
        },
        "& .MuiInputLabel-outlined.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)": {
          transform: "translate(14px, 7px) scale(1)",
        },
        // O recorte da borda acompanha o tamanho visível do rótulo flutuante.
        "& .MuiOutlinedInput-notchedOutline legend": {
          fontSize: tamanhosModaisGlobais.rotulo,
        },
      }}
    >
      {/* =====================================================
          CABEÇALHO
      ===================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          mb: 1,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,

            display: "grid",
            placeItems: "center",

            flexShrink: 0,

            borderRadius: 1.5,

            bgcolor: "var(--sear-superficie-azul, #e8f4ff)",
          }}
        >
          <AdminPanelSettingsOutlinedIcon
            aria-hidden="true"
            sx={{
              color: "var(--sear-azul, #087cf0)",
              fontSize: 27,
            }}
          />
        </Box>

        <Box>
          <Typography
            component="h1"
            sx={{
              color: coresTextosGlobais.tituloPagina,

              ...textosGlobais.tituloPagina,
              fontSize: tamanhosPaginasAdministrativas.tituloPagina,
              lineHeight: 1.15,
            }}
          >
            Meu perfil
          </Typography>

          <Typography
            sx={{
              mt: 0,

              color: "var(--sear-texto-secundario, #7b8798)",
              ...textosGlobais.textoPadrao,
              fontSize: tamanhosControlesGlobais.descricao,
            }}
          >
            Gerencie seus dados de acesso e segurança da conta.
          </Typography>
        </Box>
      </Box>

      {erro && (
        <Alert
          severity="error"
          sx={{
            mb: 0.75,
            flexShrink: 0,
          }}
        >
          {erro}
        </Alert>
      )}

      {carregando ? (
        <Box
          sx={{
            minHeight: { xs: 260, md: 0 },
            flex: "1 1 auto",

            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress size={34} />
        </Box>
      ) : (
        perfil && (
          <Box
            component="section"
            aria-label="Dados e segurança do perfil"
            tabIndex={0}
            sx={{
              display: "grid",

              flex: "1 1 auto",
              minHeight: 0,
              overflowY: { xs: "visible", md: "auto" },
              alignContent: "start",

              gridTemplateColumns: {
                xs: "1fr",
                md: "250px minmax(0, 1fr)",
              },

              gap: 1.25,

              alignItems: "start",
            }}
          >
            {/* ===============================================
                IDENTIFICAÇÃO
            =============================================== */}

            <Box
              sx={{
                width: "100%",
                minWidth: 0,

                boxSizing: "border-box",

                px: 1.75,
                py: 1.3,

                border: "1px solid var(--sear-borda, #d4e5f6)",

                borderRadius: 1.5,

                background: "linear-gradient(180deg, var(--sear-superficie-azul, #f3f9ff) 0%, var(--sear-superficie-azul, #eaf5ff) 100%)",

                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 74,
                  height: 74,

                  display: "grid",
                  placeItems: "center",

                  borderRadius: "50%",

                  bgcolor: "var(--sear-acao-azul, #087cf0)",

                  border: "5px solid var(--sear-borda, #dceeff)",
                }}
              >
                <AdminPanelSettingsOutlinedIcon
                  aria-hidden="true"
                  sx={{
                    color: "#ffffff",
                    fontSize: 43,
                  }}
                />
              </Box>

              <Typography
                sx={{
                  width: "100%",

                  mt: 0.7,

                  color: coresTextosGlobais.tituloSecao,

                  fontSize: tamanhosPaginasAdministrativas.tituloSecao,

                  fontWeight: 700,

                  lineHeight: 1.25,

                  textAlign: "center",
                }}
              >
                {perfil?.nome || "Administrador"}
              </Typography>

              <Box
                sx={{
                  mt: 0.55,

                  px: 1.3,
                  py: 0.4,

                  display: "flex",
                  alignItems: "center",

                  gap: 0.6,

                  borderRadius: 1.5,

                  bgcolor: "var(--sear-acao-azul, #087cf0)",

                  color: "#ffffff",
                }}
              >
                <AdminPanelSettingsOutlinedIcon
                  aria-hidden="true"
                  sx={{
                    fontSize: 16,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: tamanhosControlesGlobais.rotulo,
                    fontWeight: 600,
                  }}
                >
                  Administrador
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "100%",

                  mt: 1,
                  pt: 0.9,

                  borderTop: "1px solid var(--sear-borda, #cddfee)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",

                    gap: 0.7,

                    mb: 0.25,
                  }}
                >
                  <EmailOutlinedIcon
                    sx={{
                      color: "var(--sear-azul, #087cf0)",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "var(--sear-texto-secundario, #405570)",

                      fontSize: tamanhosModaisGlobais.rotulo,

                      fontWeight: 600,

                      lineHeight: 1.2,
                    }}
                  >
                    E-mail institucional
                  </Typography>
                </Box>

                <Tooltip title={perfil?.email || ""} placement="bottom" arrow>
                  <Typography
                    sx={{
                      width: "100%",

                      color: "var(--sear-azul, #1f365c)",

                      fontSize: tamanhosControlesGlobais.descricao,

                      lineHeight: 1.3,

                      whiteSpace: "nowrap",

                      overflow: "hidden",

                      textOverflow: "ellipsis",
                    }}
                  >
                    {perfil?.email || "-"}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>

            {/* ===============================================
                FORMULÁRIOS
            =============================================== */}

            <Box
              sx={{
                width: "100%",
                minWidth: 0,

                display: "grid",

                gap: 0.65,
              }}
            >
              <DadosUsuario
                perfil={perfil}
                onPerfilAtualizado={atualizarPerfilLocal}
              />

              <SegurancaUsuario />
            </Box>
          </Box>
        )
      )}
    </Box>
  );
}
